import { Link } from 'react-router-dom';
import {
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaLinkedinIn,
  FaPhoneAlt,
  FaEnvelope,
  FaYoutube,
  FaTiktok,
  FaTelegramPlane,
  FaGithub,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaClock,
  FaArrowUp,
  FaCertificate,
  FaWallet,
} from 'react-icons/fa';
import { useState, useEffect, useMemo } from 'react';
import { useSettings } from '../context/SettingsContext';
import { preloadRoute } from '../utils/routePreloader';

const iconMap = {
  facebook: { icon: <FaFacebookF />, color: 'hover:bg-[#1877F2]' },
  x: { icon: <FaTwitter />, color: 'hover:bg-[#1DA1F2]' },
  twitter: { icon: <FaTwitter />, color: 'hover:bg-[#1DA1F2]' },
  linkedin: { icon: <FaLinkedinIn />, color: 'hover:bg-[#0A66C2]' },
  youtube: { icon: <FaYoutube />, color: 'hover:bg-[#FF0000]' },
  tiktok: { icon: <FaTiktok />, color: 'hover:bg-[#000000]' },
  instagram: { icon: <FaInstagram />, color: 'hover:bg-[#E4405F]' },
  telegram: { icon: <FaTelegramPlane />, color: 'hover:bg-[#229ED9]' },
  github: { icon: <FaGithub />, color: 'hover:bg-[#333333]' },
  whatsapp: { icon: <FaWhatsapp />, color: 'hover:bg-[#25D366]' },
};

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const { settings, isVisible } = useSettings();

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setShowScrollTop(window.scrollY > 400);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const contactPhones = useMemo(() => {
    if (!settings?.contact_phones) return ['779299707'];
    try {
      const parsed = typeof settings.contact_phones === 'string' ? JSON.parse(settings.contact_phones) : settings.contact_phones;
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : ['779299707'];
    } catch (e) {
      return [settings.contact_phones];
    }
  }, [settings]);

  const socialItems = useMemo(() => {
    if (!settings?.social_channels) return [];
    try {
      const parsedSocial = typeof settings.social_channels === 'string' ? JSON.parse(settings.social_channels) : settings.social_channels;
      const activeList = [];
      Object.entries(parsedSocial || {}).forEach(([key, val]) => {
        if (val && (val.enabled === true || val.enabled === 'true') && val.url) {
          const conf = iconMap[key.toLowerCase()] || { icon: <FaLinkedinIn />, color: 'hover:bg-blue-600' };
          activeList.push({
            key,
            url: val.url,
            label: val.label || key,
            icon: conf.icon,
            color: conf.color,
          });
        }
      });
      return activeList;
    } catch (e) {
      return [];
    }
  }, [settings]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const footerLinks = {
    platform: [
      { title: 'عن داتكس', href: '/about' },
      { title: 'الدورات التدريبية', href: '/courses' },
      { title: 'المسارات التعليمية', href: '/tracks' },
      { title: 'الندوات التفاعلية', href: '/webinars' },
      { title: 'طرق الدفع', href: '/payment-methods' },
    ],
    corporate: [
      { title: 'خدماتنا الاستشارية', href: '/services' },
      { title: 'طلب تدريب مؤسسي', href: '/corporate/request' },
      { title: 'بوابة الشركات', href: '/company/login' },
      { title: 'بوابة المتدربين', href: '/trainee/login' },
    ],
  };

  return (
    <footer className="relative bg-gradient-to-br from-[#00131D] via-[#02182B] to-[#0F3D4C] text-gray-300 font-arabic dir-rtl">
      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full shadow-lg hover:shadow-blue-500/30 transition-all duration-300 hover:scale-110 flex items-center justify-center"
          aria-label="العودة للأعلى"
        >
          <FaArrowUp className="text-lg" />
        </button>
      )}

      {/* Main Footer */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-10">

            {/* Company Info */}
            <div className="lg:col-span-4 space-y-4">
              <h3 className="text-lg font-bold text-white mb-2 relative">
                {settings.site_name_ar || 'DATX للذكاء الاصطناعي وعلوم البيانات'}
                <span className="absolute -bottom-2 right-0 w-8 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full" />
              </h3>

              {isVisible('show_site_slogan', true) && (
                <p className="text-cyan-400 text-xs font-bold">
                  "{settings.site_slogan_ar || 'نجعل بياناتك تنبض بالحياة • Where data comes alive'}"
                </p>
              )}

              <p className="text-white/70 leading-relaxed text-sm font-medium">
                شركة متخصصة في تمكين الأفراد والمؤسسات من الاستفادة من قوة البيانات لاتخاذ القرارات الاستراتيجية من خلال تصميم حلول شاملة لإدارة البيانات وحوكمتها.
              </p>

              {/* Dynamic Social Links */}
              {socialItems.length > 0 ? (
                <div className="flex flex-row items-center gap-3 overflow-x-auto py-1">
                  {socialItems.map((item) => (
                    <a
                      key={item.key}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={item.label}
                      className={`w-10 h-10 shrink-0 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 ${item.color} transition-all duration-300 hover:border-transparent hover:text-white hover:scale-110`}
                    >
                      {item.icon}
                    </a>
                  ))}
                </div>
              ) : (
                <div className="flex flex-row items-center gap-3 overflow-x-auto py-1">
                  <a href="https://facebook.com/datx" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:bg-[#1877F2] hover:text-white transition-all"><FaFacebookF /></a>
                  <a href="https://x.com/datx" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:bg-[#1DA1F2] hover:text-white transition-all"><FaTwitter /></a>
                  <a href="https://linkedin.com/company/datx" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:bg-[#0A66C2] hover:text-white transition-all"><FaLinkedinIn /></a>
                </div>
              )}
            </div>

            {/* Quick Links Section */}
            <div className="lg:col-span-5">
              <h3 className="text-lg font-bold text-white mb-6 relative">
                روابط سريعة
                <span className="absolute -bottom-2 right-0 w-8 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full" />
              </h3>
              
              <div className="grid grid-cols-2 gap-4 sm:gap-6 max-w-sm">
                <ul className="space-y-3">
                  {footerLinks.platform.map((link, index) => (
                    <li key={index}>
                      <Link
                        to={link.href}
                        onMouseEnter={() => preloadRoute(link.href)}
                        onTouchStart={() => preloadRoute(link.href)}
                        className="text-white/60 hover:text-white transition-all duration-300 text-sm hover:translate-x-1 inline-flex items-center gap-2 font-medium"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500/80 shrink-0" />
                        {link.title}
                      </Link>
                    </li>
                  ))}
                </ul>

                <ul className="space-y-3">
                  {footerLinks.corporate.map((link, index) => (
                    <li key={index}>
                      <Link
                        to={link.href}
                        onMouseEnter={() => preloadRoute(link.href)}
                        onTouchStart={() => preloadRoute(link.href)}
                        className="text-white/60 hover:text-white transition-all duration-300 text-sm hover:translate-x-1 inline-flex items-center gap-2 font-medium"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400/80 shrink-0" />
                        {link.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Contact Info Section */}
            <div className="lg:col-span-3">
              <h3 className="text-lg font-bold text-white mb-6 relative">
                تواصل معنا
                <span className="absolute -bottom-2 right-0 w-8 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full" />
              </h3>
              <ul className="space-y-4">
                {/* Phones */}
                {isVisible('show_contact_phones', true) && (
                  <li className="flex items-start gap-3 group">
                    <div className="w-9 h-9 rounded-full bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors shrink-0 mt-0.5">
                      <FaPhoneAlt className="text-blue-400 text-xs" />
                    </div>
                    <div className="flex flex-col text-sm space-y-1">
                      {contactPhones.map((phone, pIdx) => (
                        <a
                          key={pIdx}
                          href={`tel:${phone}`}
                          className="text-white/80 hover:text-white transition-colors font-semibold"
                        >
                          <span dir="ltr">{phone}</span>
                        </a>
                      ))}
                    </div>
                  </li>
                )}

                {/* Email */}
                {isVisible('show_contact_email', true) && (
                  <li className="flex items-center gap-3 group">
                    <div className="w-9 h-9 rounded-full bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors shrink-0">
                      <FaEnvelope className="text-blue-400 text-xs" />
                    </div>
                    <a href={`mailto:${settings.contact_email || 'info@datxsci.com'}`} className="text-white/70 hover:text-white transition-colors text-xs font-medium">
                      {settings.contact_email || 'info@datxsci.com'}
                    </a>
                  </li>
                )}

                {/* Address */}
                {isVisible('show_contact_address', true) && (
                  <li className="flex items-center gap-3 group">
                    <div className="w-9 h-9 rounded-full bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors shrink-0">
                      <FaMapMarkerAlt className="text-blue-400 text-xs" />
                    </div>
                    <span className="text-white/70 text-xs font-medium leading-relaxed">
                      {settings.contact_address || 'صنعاء - تقاطع شارع حدة'}
                    </span>
                  </li>
                )}

                {/* Working Hours */}
                {isVisible('show_working_hours', true) && (
                  <li className="flex items-center gap-3 group">
                    <div className="w-9 h-9 rounded-full bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors shrink-0">
                      <FaClock className="text-blue-400 text-xs" />
                    </div>
                    <span className="text-white/70 text-xs font-medium leading-relaxed">
                      {settings.working_hours || 'السبت - الخميس: 9:00 ص - 6:00 م'}
                    </span>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm font-medium">
            © {currentYear} {settings.site_name_ar || 'DATX للذكاء الاصطناعي وعلوم البيانات'}. جميع الحقوق محفوظة
          </p>

          <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6">
            <Link
              to="/payment-methods"
              onMouseEnter={() => preloadRoute('/payment-methods')}
              onTouchStart={() => preloadRoute('/payment-methods')}
              className="text-xs text-emerald-400 hover:text-emerald-300 transition-all duration-200 font-bold flex items-center gap-1.5 bg-emerald-500/10 px-3.5 py-1.5 rounded-full border border-emerald-500/25 hover:border-emerald-400/40 shadow-sm hover:scale-105"
            >
              <FaWallet className="w-3.5 h-3.5 text-emerald-400" />
              <span>طرق الدفع</span>
            </Link>

            <Link
              to="/verify"
              onMouseEnter={() => preloadRoute('/verify')}
              onTouchStart={() => preloadRoute('/verify')}
              className="text-xs text-blue-400 hover:text-blue-300 transition-all duration-200 font-bold flex items-center gap-1.5 bg-blue-500/10 px-3.5 py-1.5 rounded-full border border-blue-500/20 hover:border-blue-400/40 shadow-sm hover:scale-105"
            >
              <FaCertificate className="w-3.5 h-3.5" />
              <span>التحقق من الشهادات</span>
            </Link>

            <Link
              to="/cookie-policy"
              onMouseEnter={() => preloadRoute('/cookie-policy')}
              onTouchStart={() => preloadRoute('/cookie-policy')}
              className="text-xs text-white/60 hover:text-cyan-400 transition-colors duration-200 font-medium"
            >
              سياسة ملفات الارتباط
            </Link>

            <Link
              to="/privacy-policy"
              onMouseEnter={() => preloadRoute('/privacy-policy')}
              onTouchStart={() => preloadRoute('/privacy-policy')}
              className="text-xs text-white/60 hover:text-blue-400 transition-colors duration-200 font-medium"
            >
              سياسة الخصوصية
            </Link>

            <Link
              to="/terms-of-service"
              onMouseEnter={() => preloadRoute('/terms-of-service')}
              onTouchStart={() => preloadRoute('/terms-of-service')}
              className="text-xs text-white/60 hover:text-blue-400 transition-colors duration-200 font-medium"
            >
              الشروط والأحكام
            </Link>
          </div>
        </div>
      </div>

      {/* Decorative Blur Effect */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 -left-20 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl" />
      </div>
    </footer>
  );
};

export default Footer;